import React, { useState, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Users, MapPin, Search as SearchIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AREAS } from "@/lib/constants";
import { useLanguage } from "@/context/LanguageContext";

// Lazy: react-day-picker + date-fns are heavy and the calendar only renders
// when the user opens the date popover — keep them out of the main bundle.
const Calendar = lazy(() => import("@/components/ui/calendar").then((m) => ({ default: m.Calendar })));

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const toYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const toDMon = (d) => `${d.getDate()} ${MONTHS[d.getMonth()]}`;

const HeroSearch = () => {
  const navigate = useNavigate();
  const { t, localePath } = useLanguage();
  const [area, setArea] = useState("");
  const [guests, setGuests] = useState("");
  const [range, setRange] = useState(undefined);

  const submit = () => {
    const params = new URLSearchParams();
    if (area) params.set("area_slug", area);
    if (guests) params.set("guests", guests);
    if (range?.from) params.set("check_in", toYMD(range.from));
    if (range?.to) params.set("check_out", toYMD(range.to));
    navigate(localePath(`/search?${params.toString()}`));
  };

  const dateLabel = range?.from
    ? range.to
      ? `${toDMon(range.from)} – ${toDMon(range.to)}`
      : toDMon(range.from)
    : t("booking.selectDates");

  return (
    <form
      data-testid="home-hero-search-form"
      onSubmit={(e) => { e.preventDefault(); submit(); }}
      className="w-full max-w-[980px] rounded-[18px] glass-dark p-3 sm:p-4 lux-shadow-glass"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[1.2fr_1fr_0.9fr_auto]">
        <Select value={area} onValueChange={setArea}>
          <SelectTrigger data-testid="home-search-area-select" className="h-12 bg-white/90 border-white/40 rounded-[12px] text-left">
            <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[var(--lux-seaglass)]" /><SelectValue placeholder={t("search.anyArea")} /></span>
          </SelectTrigger>
          <SelectContent>
            {AREAS.map((a) => <SelectItem key={a.slug} value={a.slug}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>

        <Popover>
          <PopoverTrigger asChild>
            <button type="button" data-testid="home-search-dates-button"
              className="h-12 bg-white/90 border border-white/40 rounded-[12px] px-3 flex items-center gap-2 text-sm text-left text-foreground">
              <CalendarDays className="h-4 w-4 text-[var(--lux-seaglass)]" /> {dateLabel}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 max-h-[80dvh] overflow-y-auto" align="start">
            <Suspense fallback={<div className="flex h-[320px] w-[300px] items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}>
              <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={1} disabled={{ before: new Date() }} />
            </Suspense>
          </PopoverContent>
        </Popover>

        <Select value={guests} onValueChange={setGuests}>
          <SelectTrigger data-testid="home-search-guests-select" className="h-12 bg-white/90 border-white/40 rounded-[12px]">
            <span className="flex items-center gap-2"><Users className="h-4 w-4 text-[var(--lux-seaglass)]" /><SelectValue placeholder={t("search.guests")} /></span>
          </SelectTrigger>
          <SelectContent>
            {[2, 4, 6, 8, 10, 12, 14, 16].map((g) => <SelectItem key={g} value={String(g)}>{g}{t("search.guestsSuffix")}</SelectItem>)}
          </SelectContent>
        </Select>

        <Button type="submit" data-testid="home-search-submit-button"
          className="h-12 px-6 bg-[var(--lux-ink)] text-[var(--lux-ivory)] hover:bg-black rounded-[12px] gap-2">
          <SearchIcon className="h-4 w-4" /> {t("nav.findVilla")}
        </Button>
      </div>
    </form>
  );
};

export default HeroSearch;
